import { IconButton, IconButtonProps } from '@chakra-ui/button'
import { Icon, IconProps } from '@chakra-ui/icon'
import { ResponsiveValue } from '@chakra-ui/react'
import { Tooltip } from '@chakra-ui/tooltip'
import React, { forwardRef, useMemo } from 'react'
import { themeColorSectionKeys } from '~/themes/colors/colors'
import { Size } from '~/types/ThemeTypes'
import { getNodeText } from '~/utils/objectUtils'

import {
  QuickActionButtonVariants,
  useQuickActionButtonStyles,
} from './componentStyles/QuickActionButton.style'

const { Tooltip: TooltipColors } = themeColorSectionKeys.shared

export type QuickActionButtonSizes =
  | Size.xs
  | Size.sm
  | Size.md
  | Size.lg
  | Size.xl

interface QuickActionButtonProps extends Omit<IconButtonProps, 'aria-label'> {
  variant: QuickActionButtonVariants
  size?: ResponsiveValue<QuickActionButtonSizes>
  actionIcon: SVGComponent
  tooltipText: string | React.ReactNode
  tooltipDisabled?: boolean
  iconProps?: IconProps
}
export const QuickActionButton = forwardRef(function QuickActionButton(
  {
    variant,
    size,
    actionIcon,
    tooltipText,
    tooltipDisabled,
    iconProps,
    disabled,
    onMouseEnter,
    onMouseLeave,
    ...rest
  }: QuickActionButtonProps,
  ref: React.Ref<HTMLButtonElement> | undefined,
) {
  const [isHovered, setIsHovered] = React.useState(false)

  const tooltipAriaLabel = useMemo(() => {
    return getNodeText(tooltipText)
  }, [tooltipText])

  const styles = useQuickActionButtonStyles({ variant, size })
  return (
    <Tooltip
      isOpen={isHovered && tooltipDisabled !== true}
      hasArrow
      placement="top"
      label={tooltipText}
      bg={TooltipColors.Background}
      color={TooltipColors.FontColor}
      sx={styles.tooltip}
      isDisabled={disabled}
    >
      <IconButton
        data-group
        ref={ref}
        __css={styles.iconButton}
        aria-label={tooltipAriaLabel}
        isDisabled={disabled}
        {...rest}
        onMouseEnter={(e) => {
          setIsHovered(true)
          if (onMouseEnter) onMouseEnter(e)
        }}
        onMouseLeave={(e) => {
          setIsHovered(false)
          if (onMouseLeave) onMouseLeave(e)
        }}
      >
        <Icon sx={{ ...styles.icon, ...iconProps }} as={actionIcon} />
      </IconButton>
    </Tooltip>
  )
})
